import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';

import { DefaultIcon, SDate } from '@summit/core';
import {
  DateRange,
  DateRangeType,
  IUserExperience,
  SummitDisposing,
  SummitUserExperienceService,
  Week
} from '@summit/models';
import { LayoutService } from '@summit/ui';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { FloatLabel } from 'primeng/floatlabel';

@Component({
  selector: 's-form-input-month-range',
  templateUrl: './s-form-input-month-range.component.html',
  styleUrls: ['./s-form-input-month-range.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule, FloatLabel, DatePicker, Button]
})
export class SFormInputMonthRangeComponent extends SummitDisposing implements OnInit {
  @Input() label: string;
  @Input() labelLocation: 'over' | 'in' | 'on' = 'over';
  @Input() required: boolean;
  @Input() size: 'small' | 'large';

  @Input() defaultWeekOffset = 0;
  @Input() defaultRange: DateRange;
  @Input() defaultRangeType: DateRangeType = DateRangeType.TenantWeek;
  @ViewChild('calendar') calendar: DatePicker;
  @Output()
  DateRangeChange: EventEmitter<DateRange> = new EventEmitter<DateRange>();
  private dateRangeFocus = false;
  public fieldName = 'weekRange';
  public controlValue?: Date[];
  public rangeValue: DateRange;
  private calendarVisible = false;
  defaultIcon = DefaultIcon;

  constructor(
    fb: FormBuilder,
    private uxService: SummitUserExperienceService<IUserExperience>,
    protected layoutService: LayoutService
  ) {
    super();
  }

  ngOnInit() {
    /*
    console.log(
      `[RangePicker] Initializing with ${this.defaultRange?.begin?.format('MM/DD')} & ${this.defaultRange?.end?.format(
        'MM/DD'
      )}`,
      this.defaultRange,
      this.defaultRangeType
    );
    */
    this.defaultRangeType = this.defaultRangeType ?? DateRangeType.TenantWeek;
    if (this.defaultRange) {
      //console.log(`[RangePicker] Initializing with ${this.defaultRange?.begin?.format('MM/DD')}`);
      this.defaultRange.begin = this.defaultRange.begin.startOf('month');
      this.defaultRange.end = this.defaultRange.end.endOf('month');
      this.updateRange(this.defaultRange);
    } else {
      const selWeek = new Week(this.defaultWeekOffset, this.defaultRangeType);
      //console.log(`[RangePicker] Initializing with current month`);
      this.updateRange(
        new DateRange(
          {
            begin: selWeek.start.startOf('month'),
            end: selWeek.end.endOf('month')
          },
          this.defaultRangeType
        )
      );
    }
    this.updateRange;
  }

  public onShow() {
    this.calendarVisible = true;
  }
  public onClose() {
    this.calendarVisible = false;
  }

  public ProcessRangeChange() {
    if (!this.rangeValue?.begin) {
      return;
    }
    const beginSource = SDate.ShiftTZToUTC(this.rangeValue.begin) ?? this.rangeValue.begin;
    const endSource = SDate.ShiftTZToUTC(this.rangeValue.end ?? this.rangeValue.begin) ?? this.rangeValue.begin;
    const begin = SDate.newUtc(beginSource);
    const end = SDate.newUtc(endSource).endOf('month');
    // console.log(`[RangePicker] Processing Range Change: ${begin.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`);
    this.updateRange(new DateRange({ begin: begin, end: end }));
  }

  private getDefaultDateRange(): DateRange {
    return this.defaultRange ?? new Week(0, this.defaultRangeType).ToDateRange(this.defaultRangeType);
  }

  updateRange(newRange?: DateRange) {
    newRange = newRange ? newRange : this.getDefaultDateRange();
    if (newRange.end.date() != newRange.end.daysInMonth()) {
      newRange.end = newRange.end.endOf('month');
    }
    if (!this.rangeValue || !newRange.end.isSame(this.rangeValue.end)) {
      const begin = SDate.ShiftTZToLocal(newRange.begin);
      const end = SDate.ShiftTZToLocal(newRange.end);
      this.controlValue = begin && end ? [begin, end] : undefined;
      this.rangeValue = newRange;
      // console.log(
      //   `[RangePicker] Updating Range: ${this.rangeValue.begin.format('MM/DD/YYYY')} - ${this.rangeValue.end.format(
      //     'MM/DD/YYYY'
      //   )}`
      // );
      this.DateRangeChange.emit(new DateRange(this.rangeValue));
    }
  }
  moveToToday() {
    const newRange = new DateRange(this.defaultRangeType);
    // console.log(`Moving to Today: ${newRange.begin.format()}-${newRange.end.format()}`);
    this.updateRange(newRange);
  }

  moveBackWeek() {
    // Enter key in input is triggering this so added a if to separate decision
    if (!this.dateRangeFocus) {
      const currentValue = new DateRange(this.rangeValue);
      currentValue.begin = currentValue.begin.add(-1, 'month');
      currentValue.end = currentValue.end.add(-1, 'month');
      this.updateRange(currentValue);
    } else {
      this.ProcessRangeChange();
    }
  }

  moveForwardWeek() {
    //console.log('Forward a week');
    const currentValue = new DateRange(this.rangeValue);
    currentValue.begin = currentValue.begin.add(1, 'month');
    currentValue.end = currentValue.end.add(1, 'month');
    this.updateRange(currentValue);
  }

  getRange(): DateRange {
    return new DateRange(this.rangeValue);
  }
  /*
  setFocusTracking(newFocusState: boolean) {
    if (!newFocusState) {
      //console.log(`Focus changed calling ProcessRange`);
      this.ProcessRangeChange();
    }
    this.dateRangeFocus = newFocusState;
  }
*/
  onChange() {
    //console.log(`${this.fieldName.toString()}: onChange`, this.controlValue);
    if (!this.controlValue || !this.controlValue[1]) return;
    this.rangeValue = new DateRange({
      begin: SDate.newUtc(this.controlValue?.[0]),
      end: SDate.newUtc(this.controlValue?.[1])
    });
    //console.log(`${this.fieldName.toString()}: onChange`, this.rangeValue);
    this.ProcessRangeChange();
    if (this.calendarVisible) {
      this.calendarVisible = false;
      this.calendar?.toggle();
    }
  }
}
